import { useEffect, useState } from 'react'

import {toast} from '@dd3tech/wiggui/Toast'
import { useMyLocation } from '../hooks/useMyLocation'
import { GoogleMapsStructuredPlace } from '../interfaces/googleMapsInterfaces'
import LoadIcon from './LoadIcon'
import { MdOutlineNearMe, MdOutlineSearch } from 'react-icons/md'
import { useColdStartStore } from '../store'
import { composeClasses } from '@wiggot/utils/src/strings'

const validateUserLocation = (
  userAddress: string,
  limitedRegions?: string[]
): boolean => {
  if (!limitedRegions?.length) {
    return true
  }
  const rgxRegion = new RegExp(limitedRegions.join('|'), 'i')
  return rgxRegion.test(userAddress)
}

interface Props {
  /**
   * This property indicates whether the component is loading.
   * @type {boolean}
   */
  isLoading?: boolean

  /**
   * This property indicates whether MyLocation is disabled.
   * @type {boolean}
   */
  isDisabled?: boolean

  /**
   * This property indicates whether the MyLocation is visible.
   * @type {boolean}
   */
  isVisible: boolean

  /**
   * This property indicates whether the MyLocation is shown as an inline element, next to the input field.
   * @type {boolean}
   */
  showInline?: boolean

  /**
   * Limit the user location functionality to certain regions.
   * @type {string}
   */
  limitedRegions?: string[]

  /**
   * This function is called when the match is selected.
   * @type {(place: GoogleMapsStructuredPlace) => void}
   */
  onSelected: (place: GoogleMapsStructuredPlace) => void

  /**
   * Styles for location's label.
   * @type {string}
   */
  labelClassName?: string

  /**
   * Styles for location's label icon.
   * @type {string}
   */
  iconLabelClassName?: string

  enableDefaultSearches?: boolean
}

const MyLocation = ({
  onSelected,
  isVisible,
  isLoading,
  isDisabled,
  showInline = false,
  limitedRegions,
  labelClassName,
  iconLabelClassName,
  enableDefaultSearches
}: Props) => {
  const { getPosition } = useMyLocation()

  const { isUsedAutocomplete } = useColdStartStore()

  const [load, setLoader] = useState(false)

  /**
   * This function is called when the user clicks on the location button.
   * @returns {Promise<void>}
   */
  const select = async (): Promise<void> => {
    setLoader(true)
    try {
      const place = await getPosition()

      setLoader(false)
      if (!place) {
        return
      }

      if (!validateUserLocation(place.fullAddress, limitedRegions)) {
        toast({
          type: 'warning',
          title: 'Este servicio aún no está disponible en tu ubicación actual.',
        })
        return
      }

      onSelected(place)
    } catch (error: any) {
      if (error?.code === 1) {
        toast({
          type: 'warning',
          title:
            'Monopolio no tiene permiso para acceder a tu ubicación actual.',
        })
        setLoader(false)
      }
    }
  }

  /**
   * This function is responsible for handling the keydown event.
   * @param event - This is the event to handle.
   * @returns {Promise<void>}
   */
  const handleKeyDown = async (event: any): Promise<void> => {
    if (enableDefaultSearches && !isUsedAutocomplete) {
      return
    }

    if (event.key === 'Enter' && isVisible) {
      select()
    }
  }

  useEffect(() => {
    if (!showInline) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isVisible, showInline])

  return (
    <button
      type="button"
      className={composeClasses(
        'flex justify-between items-center',
        !showInline && 'w-full'
      )}
      disabled={isDisabled}
      onClick={select}
    >
      {!showInline && (
        <div className="flex justify-start items-center gap-x-[8px]">
          <MdOutlineSearch
            className={composeClasses(
              'w-[18px] h-[18px] !text-lg !flex justify-center items-center text-[#121212]',
              iconLabelClassName && iconLabelClassName
            )}
          />
          <p
            className={composeClasses(
              'text-xs text-text-primary',
              labelClassName && labelClassName
            )}
          >
            Usar mi ubicación
          </p>
        </div>
      )}

      <div
        className={composeClasses(
          'w-10 h-10 rounded-full flex justify-center items-center',
          isDisabled
            ? 'bg-tertiary'
            : 'bg-button-blue-01 hover:bg-button-blue-02 duration-200 active:scale-95'
        )}
      >
        {load || isLoading ? (
          <div role="status">
            <LoadIcon />
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          <MdOutlineNearMe
            className={composeClasses(
              'w-[18px] h-[18px]',
              isDisabled ? 'text-primary opacity-50' : 'text-button-primary'
            )}
          />
        )}
      </div>
    </button>
  )
}

export default MyLocation
